import React, { useEffect, useMemo, useState } from "react";
import { get } from "aws-amplify/api";
import "./RaceResult.css";

const PAYOUT_LABELS = {
  win: "単勝",
  place: "複勝",
  quinella: "馬連",
  trio: "3連複",
  trifecta: "3連単",
};

const POLL_INTERVAL_MS = 60 * 1000;

const toJstDateKey = (date = new Date()) =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  })
    .format(date)
    .replaceAll("-", "");

export const getRaceStartAt = (raceId, hassoTime) => {
  const id = String(raceId || "");
  const time = String(hassoTime || "").padStart(4, "0");

  if (!/^\d{12}$/.test(id) || !/^\d{4}$/.test(time)) return null;

  const year = id.slice(0, 4);
  const month = id.slice(4, 6);
  const day = id.slice(6, 8);
  const hour = time.slice(0, 2);
  const minute = time.slice(2, 4);
  const start = new Date(`${year}-${month}-${day}T${hour}:${minute}:00+09:00`);

  return Number.isNaN(start.getTime()) ? null : start;
};

export const hasRaceStarted = (raceId, hassoTime, now = new Date()) => {
  const start = getRaceStartAt(raceId, hassoTime);
  return start ? now.getTime() >= start.getTime() : true;
};

const formatRaceDate = (raceId) => {
  const id = String(raceId || "");
  if (!/^\d{12}$/.test(id)) return "";
  return `${Number(id.slice(4, 6))}月${Number(id.slice(6, 8))}日`;
};

const numberBadgeStyle = (wakuColors, waku) => {
  const color = wakuColors?.[Number(waku)] || {};
  return {
    background: color.bg || "#f2f3f5",
    color: color.text || "#111",
    borderColor: color.border || "#d5d7db",
  };
};

const PayoutRows = ({ payouts }) => {
  const rows = Object.keys(PAYOUT_LABELS).flatMap((key) =>
    (payouts?.[key] || []).map((item, index) => ({
      key: `${key}-${index}`,
      label: PAYOUT_LABELS[key],
      ...item,
    }))
  );

  if (!rows.length) return null;

  return (
    <section className="race-result-payouts" aria-label="主な払戻金">
      <h4>主な払戻金</h4>
      <div className="race-result-payout-list">
        {rows.map((row) => (
          <div className="race-result-payout-row" key={row.key}>
            <span className="race-result-payout-label">{row.label}</span>
            <span className="race-result-payout-combination">{row.combination}</span>
            <strong>{row.amount}</strong>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function RaceResult({
  raceId,
  raceInfo,
  placeName,
  wakuColors,
}) {
  const [result, setResult] = useState(null);
  const [state, setState] = useState("idle");
  const [isOpen, setIsOpen] = useState(false);
  const hassoTime = raceInfo?.HassoTime;

  const isRaceToday = useMemo(
    () => String(raceId || "").slice(0, 8) === toJstDateKey(),
    [raceId]
  );

  useEffect(() => {
    let disposed = false;
    let timer = null;
    let operation = null;

    setResult(null);
    setState("idle");
    setIsOpen(false);

    const schedule = (delay) => {
      timer = window.setTimeout(load, Math.max(1000, delay));
    };

    const load = async () => {
      if (disposed || !raceId) return;

      const startAt = getRaceStartAt(raceId, hassoTime);
      const now = new Date();

      if (startAt && now < startAt) {
        setState("before");
        schedule(Math.min(startAt.getTime() - now.getTime() + 5000, 60000));
        return;
      }

      setState((current) => (current === "idle" || current === "before" ? "loading" : current));

      try {
        operation = get({
          apiName: "readRaceCsvdev",
          path: "/getRaceResult",
          options: {
            queryParams: { race_id: raceId },
          },
        });

        const { body } = await operation.response;
        const data = await body.json();

        if (disposed || data?.race_id !== raceId) return;

        setResult(data);
        setState(data?.status || "error");

        if (data?.status === "pending" && isRaceToday) {
          schedule(POLL_INTERVAL_MS);
        }
      } catch (error) {
        if (disposed) return;
        console.error("レース結果取得エラー:", error);
        setState("error");
      }
    };

    load();

    return () => {
      disposed = true;
      if (timer) window.clearTimeout(timer);
      if (operation?.cancel) operation.cancel();
    };
  }, [raceId, hassoTime, isRaceToday]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  if (state === "before" || state === "idle") return null;

  if (state === "loading") {
    return (
      <span className="race-result-pill race-result-pill-waiting" aria-live="polite">
        結果確認中…
      </span>
    );
  }

  if (state === "pending") {
    return (
      <span className="race-result-pill race-result-pill-waiting" aria-live="polite">
        結果待ち…
      </span>
    );
  }

  if (state === "unavailable") return null;

  if (state === "error") {
    return (
      <span className="race-result-pill race-result-pill-error" title="レース結果を取得できませんでした">
        結果取得エラー
      </span>
    );
  }

  const top3 = result?.top3 || [];
  const summary = top3.map((horse) => horse.umaban).join("-");

  if (state !== "final" || !top3.length) return null;

  return (
    <>
      <button
        type="button"
        className="race-result-pill race-result-pill-final"
        onClick={() => setIsOpen(true)}
        aria-haspopup="dialog"
      >
        <span aria-hidden="true">✓</span> 確定 {summary}
        <span className="race-result-pill-chevron" aria-hidden="true">⌄</span>
      </button>

      {isOpen && (
        <div
          className="race-result-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false);
          }}
        >
          <section
            className="race-result-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="race-result-title"
          >
            <div className="race-result-handle" aria-hidden="true" />
            <header className="race-result-header">
              <div>
                <h3 id="race-result-title">レース結果</h3>
                <p>
                  {formatRaceDate(raceId)} {placeName || ""} {raceInfo?.RaceNum || ""}R
                  {raceInfo?.Hondai ? ` ${raceInfo.Hondai}` : ""}
                </p>
                <small>
                  {raceInfo?.芝ダ || ""}{raceInfo?.Kyori || ""}m
                  {hassoTime ? ` ${hassoTime.slice(0, 2)}:${hassoTime.slice(2)}発走` : ""}
                </small>
              </div>
              <button
                type="button"
                className="race-result-close"
                onClick={() => setIsOpen(false)}
                aria-label="レース結果を閉じる"
              >
                ×
              </button>
            </header>

            <section className="race-result-finishers" aria-label="上位3頭">
              {top3.map((horse, index) => (
                <div className="race-result-finisher" key={`${horse.rank}-${horse.umaban}`}>
                  <span className={`race-result-rank rank-${Math.min(index + 1, 3)}`}>
                    {horse.rank}着
                  </span>
                  <span
                    className="race-result-number"
                    style={numberBadgeStyle(wakuColors, horse.waku)}
                  >
                    {horse.umaban}
                  </span>
                  <span className="race-result-horse">
                    <strong>{horse.name}</strong>
                    <small>{horse.jockey || ""}</small>
                  </span>
                  <span className="race-result-time">
                    <strong>{horse.time || "—"}</strong>
                    <small>{horse.popularity ? `${horse.popularity}人気` : ""}</small>
                  </span>
                </div>
              ))}
            </section>

            <PayoutRows payouts={result?.payouts} />

            {result?.source_url && (
              <div className="race-result-source">
                <a href={result.source_url} target="_blank" rel="noreferrer">
                  JRA公式で詳細結果を確認 ↗
                </a>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
